import {
	IconBolt,
	IconBookmark,
	IconDeviceLaptop,
	IconEyeOff,
	IconKey,
	IconUsers,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Do I need to create an account to use ineedatest.email?",
		answer:
			"No. There is no account, no password, no email address required to sign up. Open the site, type a local part, pick a domain, set an expiry, and the inbox is live. The URL is the only credential — bookmark it or copy it, and you can return to the inbox from any device.",
	},
	{
		question:
			"How is this different from other 'no registration' email services?",
		answer:
			"Most no-registration email services give you a random address in a shared, public inbox — anyone who guesses the address can read your mail. ineedatest.email gives you a private inbox. The URL is not guessable from the local part alone, and there is no public directory. You choose the local part, the domain, and when it expires.",
	},
	{
		question: "If there is no account, how do I get back to my inbox?",
		answer:
			"Bookmark the inbox URL, or copy it before you leave. The URL contains all the information needed to retrieve the inbox — no session, no cookie, no login. If you lose the URL before the inbox expires, you can reconstruct it from the local part, domain, and expiry you used when you created it.",
	},
	{
		question: "Can I share the inbox URL with someone else?",
		answer:
			"Yes. Because the inbox URL is the credential, anyone with the URL can read the inbox. This is useful for sharing a test inbox with a colleague — paste the URL in Slack and they can open it directly. It also means you should not use this for anything sensitive that should stay private.",
	},
	{
		question:
			"Does not having an account mean there is no data stored about me?",
		answer:
			"Correct. ineedatest.email does not collect names, passwords, IP addresses linked to identities, or billing information. Incoming emails are stored only until the inbox expires, then deleted. There is no user record to associate them with.",
	},
];

const reasons = [
	{
		icon: IconBolt,
		title: "Instant — no form to fill in",
		body: "Most services that say 'no registration' still have a sign-up form. ineedatest.email has none. Type a local part, pick a domain, pick an expiry. That is it. The inbox is live before you finish reading this sentence.",
	},
	{
		icon: IconKey,
		title: "The URL is the inbox",
		body: "There is no password, no session, no cookie to manage. The inbox URL is the credential. Share it, bookmark it, or open it in a new browser — it works anywhere, without logging in.",
	},
	{
		icon: IconEyeOff,
		title: "Private by default",
		body: "No-registration inboxes on most services are public and guessable. ineedatest.email inboxes are private: the URL is not listed, not searchable, and not guessable. Mail goes nowhere else.",
	},
	{
		icon: IconBookmark,
		title: "Persistent across sessions",
		body: "Unlike tab-based or session-based disposable services, ineedatest.email inboxes persist for the duration you chose — days, weeks, or longer. Close the tab and come back later. The inbox is still there.",
	},
	{
		icon: IconDeviceLaptop,
		title: "Works on any device",
		body: "No app to install, no account to sync. Open the inbox URL on your phone, tablet, or desktop. Because there is no session, switching devices is trivial — paste the URL and the inbox opens.",
	},
	{
		icon: IconUsers,
		title: "Shareable with a team",
		body: "Need a shared throwaway inbox for a team QA session or a shared test scenario? Paste the inbox URL in your team chat. Everyone who has the URL can read the inbox — no invitations, no shared credentials to manage.",
	},
];

export const Route = createFileRoute("/_marketing/for/no-registration")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Disposable email with no registration — ineedatest.email",
			description:
				"Get a private throwaway inbox in seconds. No account, no password, no form to fill in. The inbox URL is the only credential.",
			keywords:
				"disposable email no registration, temporary email no sign up, throwaway email no account, anonymous email no registration, temp mail no registration",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Disposable email with no registration</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							No account required
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Disposable email with no registration
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							No sign-up form. No password. No account. Open the site, pick an
							address, and the inbox is live. The URL is the only thing you need
							— bookmark it and come back any time before it expires.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* What "no registration" actually means */}
			<section className="border-t border-border/50 bg-muted/15 py-12 sm:py-16">
				<MarketingContainer>
					<div className="max-w-2xl">
						<p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary">
							What this means
						</p>
						<h2 className="mb-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
							Most "no registration" email services still require something
						</h2>
						<p className="mb-4 text-base text-pretty text-muted-foreground">
							Many services advertise no-registration inboxes, then present a
							public shared inbox where anyone who guesses your address can read
							your mail. Others give you a random address you cannot remember,
							tied to a browser session that disappears when you close the tab.
						</p>
						<p className="text-base text-pretty text-muted-foreground">
							ineedatest.email works differently. You choose the local part —
							something you can remember. You set the expiry. The inbox is
							private: not listed, not guessable, not shared. The URL is the
							credential, and it works from any device without a login.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Why it works */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Why it works"
						heading="Six things that make no-registration email actually useful"
						description="The usual no-registration tradeoffs — public inboxes, random addresses, session-bound expiry — are not tradeoffs here."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{reasons.map((item, index) => {
							const Icon = item.icon;
							return (
								<div
									key={item.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.08}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{item.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{item.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* How to use */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="How to use it"
						heading="Three steps, no form"
						className="mb-10 sm:mb-12"
					/>
					<div className="flex flex-col gap-5">
						{[
							{
								step: "1",
								title: "Choose an address",
								body: 'Open ineedatest.email. Type a local part — anything you will remember, like "acme-test" or "newsletter-jan". Pick a receiving domain and set an expiry. No form, no email address, no password.',
							},
							{
								step: "2",
								title: "Copy the inbox URL",
								body: "The inbox URL is generated immediately. Copy it or bookmark it — this URL is the only way to access this inbox. You can open it in any browser, on any device, without logging in.",
							},
							{
								step: "3",
								title: "Use the address, check the inbox",
								body: "Paste the address wherever you need it. Mail arrives in the inbox within seconds. When you are done, do nothing — the inbox expires automatically and all mail is deleted.",
							},
						].map((item) => (
							<div
								key={item.step}
								className="flex gap-5 rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40 text-sm font-semibold text-primary">
									{item.step}
								</div>
								<div className="flex min-w-0 flex-col gap-1">
									<h3 className="text-base font-semibold text-foreground">
										{item.title}
									</h3>
									<p className="text-sm text-pretty text-muted-foreground">
										{item.body}
									</p>
								</div>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about no-registration disposable email"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + related */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								No account. No form. No wait.
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Pick an address, set an expiry, get a private inbox in seconds.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							More on ineedatest.email:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Other ways to use disposable email
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/for/sign-up"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For sign-ups
							</a>
							<a
								href="/for/testing"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For email testing
							</a>
							<a
								href="/for/testing"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For OTP codes
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
